import React, { useState, useCallback, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  getAnalytics, getAnalyticsChartData,
  getConversionFunnel, getTopLocations,
  getTopScholars, getRetentionData, getReligionDistribution,
} from "../services/adminService";
import API from "../services/api";
import {
  pageContainer, headerStyle, breadcrumb, titleStyle, subtitleStyle,
  cardStyle, statsGrid, labelStyle, valueStyle, subTextStyle,
  sectionTitle, primaryBtn, funnelContainer,
} from "../styles/theme";
import Toast from "../components/Toast";
import { useToast, useAdminPage } from "../utils/useAdminPage";
import { useIsMobile } from "../utils/useIsMobile";
import ChartFilterBar, { ChartPeriod } from "../components/Charts/ChartFilterBar";

// ── Colour palette ────────────────────────────────────────────
const PIE_COLORS = ["#3b82f6", "#f97316", "#8b5cf6", "#10b981", "#f59e0b", "#ec4899"];

// ── Trend badge ────────────────────────────────────────────────
const TrendBadge = ({ pct }: { pct: number }) => {
  const up = pct >= 0;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 2,
      background: up ? "#f0fdf4" : "#fef2f2",
      color: up ? "#15803d" : "#b91c1c",
      fontSize: 11, fontWeight: 700,
      padding: "3px 8px", borderRadius: 20,
    }}>
      {up ? "▲" : "▼"} {Math.abs(pct)}%
    </span>
  );
};

// ── Stat Card ──────────────────────────────────────────────────
interface StatCardProps {
  label: string; val: string; sub: string;
  icon: string; color: string; trend?: number; highlight?: boolean;
}
const StatCard = ({ label, val, sub, icon, color, trend, highlight }: StatCardProps) => (
  <div style={{
    ...cardStyle,
    ...(highlight ? {
      background: `linear-gradient(135deg, ${color}, ${color}cc)`,
      border: "none",
    } : {}),
    position: "relative", overflow: "hidden",
  }}>
    {/* background glow */}
    <div style={{
      position: "absolute", top: -20, right: -20,
      width: 80, height: 80, borderRadius: "50%",
      background: highlight ? "rgba(255,255,255,0.1)" : `${color}12`,
    }} />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: highlight ? "rgba(255,255,255,0.2)" : `${color}18`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18,
      }}>{icon}</div>
      {trend !== undefined && <TrendBadge pct={trend} />}
    </div>
    <div style={{ ...labelStyle, color: highlight ? "rgba(255,255,255,0.85)" : undefined }}>{label}</div>
    <div style={{ ...valueStyle, color: highlight ? "#fff" : undefined, fontSize: 26, margin: "6px 0" }}>{val}</div>
    <div style={{ ...subTextStyle, color: highlight ? "rgba(255,255,255,0.7)" : undefined }}>{sub}</div>
  </div>
);

// ── Funnel Step ────────────────────────────────────────────────
const FunnelStep = ({ label, val, widthPct, color, total }: any) => {
  const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <div style={{ width: 130, fontSize: 12, color: "#64748b", fontWeight: 600, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, height: 36, position: "relative", overflow: "hidden" }}>
        <div style={{
          width: `${Math.max(widthPct, 4)}%`, height: "100%",
          background: color, borderRadius: 6,
          transition: "width 1s cubic-bezier(0.34,1.56,0.64,1)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", padding: "0 8px" }}>{val.toLocaleString()}</span>
        </div>
      </div>
      <div style={{ width: 42, fontSize: 12, color: "#64748b", fontWeight: 600, textAlign: "right" }}>{pct}%</div>
    </div>
  );
};

// ── Location Item ──────────────────────────────────────────────
const LocationItem = ({ rank, name, sub, val }: any) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, background: "#eff6ff",
        color: "#2563eb", display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0,
      }}>{rank}</div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{name}</div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div>
      </div>
    </div>
    <div style={{ textAlign: "right" }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>{val}</div>
      <div style={{ fontSize: 11, color: "#94a3b8" }}>scholars</div>
    </div>
  </div>
);

// ── Scholar Item ───────────────────────────────────────────────
const ScholarItem = ({ rank, name, religion, rating, bookings, earnings }: any) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        background: `hsl(${rank * 47}, 65%, 92%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: 800, color: `hsl(${rank * 47}, 55%, 35%)`,
        flexShrink: 0,
      }}>{name?.charAt(0) || "S"}</div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{name || "—"}</div>
        <div style={{ fontSize: 11, color: "#64748b" }}>
          Rating {rating?.toFixed(1) || "N/A"} · {bookings} bookings · {religion}
        </div>
      </div>
    </div>
    <div style={{ color: "#10b981", fontWeight: 700, fontSize: 14 }}>{earnings}</div>
  </div>
);

// ── Retention Bar ──────────────────────────────────────────────
const RetentionBar = ({ label, pct, color }: any) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
      <span style={{ fontWeight: 600, color: "#475569" }}>{label}</span>
      <span style={{ fontWeight: 800, color }}>{pct}%</span>
    </div>
    <div style={{ height: 8, background: "#f1f5f9", borderRadius: 10 }}>
      <div style={{
        width: `${Math.min(pct, 100)}%`, height: "100%",
        background: color, borderRadius: 10,
        transition: "width 1s cubic-bezier(0.34,1.56,0.64,1)",
      }} />
    </div>
  </div>
);

// ── Custom Pie label ───────────────────────────────────────────
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, percent }: any) => {
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  if (percent < 0.05) return null;
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── Main Component ─────────────────────────────────────────────
const Analytics = () => {
  const [period] = useState("30d");
  const [revPeriod, setRevPeriod] = useState<ChartPeriod>("month");
  const [demoPeriod, setDemoPeriod] = useState<ChartPeriod>("all");
  const [retPeriod, setRetPeriod]  = useState<ChartPeriod>("month");
  const [revChartData, setRevChartData] = useState<any[]>([]);
  const [revChartLoading, setRevChartLoading] = useState(false);
  const isMobile = useIsMobile();
  const { toast, showToast } = useToast();

  // Fetch revenue chart whenever period changes
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      setRevChartLoading(true);
      try {
        const res = await API.get(`/admin/revenue-chart?period=${revPeriod}`);
        if (!cancelled && res.data?.success) setRevChartData(res.data.data || []);
      } catch { /* silent */ } finally {
        if (!cancelled) setRevChartLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [revPeriod]);

  const fetcher = useCallback(async () => {
    try {
      const [s, chart, f, loc, sch, ret, rel] = await Promise.all([
        getAnalytics(period),
        getAnalyticsChartData(6),
        getConversionFunnel(),
        getTopLocations(5),
        getTopScholars(5),
        getRetentionData(),
        getReligionDistribution(),
      ]);
      return {
        stats: s || null,
        chartData: chart || [],
        funnel: f || null,
        locations: loc || [],
        scholars: sch || [],
        retention: ret || null,
        religionDist: rel?.users || [],
        operationalMetrics: s?.operationalMetrics || null,
      };
    } catch {
      showToast("Failed to load analytics data", "error");
      return {
        stats: null,
        chartData: [],
        funnel: null,
        locations: [],
        scholars: [],
        retention: null,
        religionDist: [],
      };
    }
  }, [period, showToast]);

  const { data, loading, revalidating, refresh, syncLabel } = useAdminPage(
    {
      stats: null,
      chartData: [],
      funnel: null,
      locations: [],
      scholars: [],
      retention: null,
      religionDist: [],
      operationalMetrics: null,
    },
    {
      cacheNs: "analytics",
      fetcher,
      cacheKeySuffix: `:${period}`,
    }
  );

  const { stats, funnel, locations, scholars, retention, religionDist, operationalMetrics } = data;

  // ── Stats cards ────────────────────────────────────────────
  const metricCards = [
    {
      label: "Total Users", icon: "US", color: "#2563eb", highlight: true,
      val: stats?.totalUsers?.toLocaleString() || "—",
      sub: `+${stats?.periodCurrent?.users || 0} this period`,
      trend: stats?.trends?.users,
    },
    {
      label: "Total Bookings", icon: "BK", color: "#10b981",
      val: stats?.totalBookings?.toLocaleString() || "—",
      sub: `+${stats?.periodCurrent?.bookings || 0} this period`,
      trend: stats?.trends?.bookings,
    },
    {
      label: "Total Revenue", icon: "RV", color: "#8b5cf6",
      val: stats ? `₹${(stats.totalRevenue || 0).toLocaleString()}` : "—",
      sub: `₹${(stats?.periodCurrent?.revenue || 0).toLocaleString()} this period`,
      trend: stats?.trends?.revenue,
    },
    {
      label: "Active Scholars", icon: "SC", color: "#f59e0b",
      val: stats?.totalScholars?.toLocaleString() || "—",
      sub: `+${stats?.periodCurrent?.scholars || 0} this period`,
      trend: stats?.trends?.scholars,
    },
  ];

  // ── Funnel data ─────────────────────────────────────────────
  const funnelBase = funnel?.totalUsers || 1;
  const funnelSteps = [
    { label: "App Users",        val: funnel?.totalUsers    || 0, color: "#3b82f6" },
    { label: "Verified Sign-ups",val: funnel?.signedUp      || 0, color: "#06b6d4" },
    { label: "Created Profile",  val: funnel?.viewedServices|| 0, color: "#10b981" },
    { label: "Made Booking",     val: funnel?.madeBooking   || 0, color: "#f59e0b" },
    { label: "Completed",        val: funnel?.completedBooking || 0, color: "#8b5cf6" },
  ];

  // ── Religion pie data ───────────────────────────────────────
  const pieData = religionDist.map((r: any) => ({
    name: r.religion, value: r.count,
  }));

  return (
    <div style={pageContainer}>
      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
        @keyframes spin    { to  { transform:rotate(360deg) } }
      `}</style>
      <Toast toast={toast} />

      {/* ── Header ── */}
      <div style={headerStyle}>
        <div>
          <div style={breadcrumb}>Dashboard {">"}  <strong>Analytics</strong></div>
          <h1 style={titleStyle}>Platform Analytics</h1>
          <p style={subtitleStyle}>Deep insights into user behaviour, conversion metrics, and growth trends.</p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {syncLabel && (
            <span style={{ fontSize: 12, color: "#94a3b8" }}>
              Synced {syncLabel}
            </span>
          )}
          <button
            style={{
              ...primaryBtn,
              display: "flex", alignItems: "center", gap: 6,
              opacity: loading || revalidating ? 0.7 : 1,
            }}
            onClick={refresh}
            disabled={loading || revalidating}
          >
            {loading || revalidating ? (
              <span style={{
                width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)",
                borderTopColor: "#fff", borderRadius: "50%",
                display: "inline-block", animation: "spin 0.7s linear infinite",
              }} />
            ) : ""} Refresh
          </button>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div style={statsGrid}>
        {metricCards.map((m, i) => (
          <StatCard key={i} {...m} />
        ))}
      </div>

      {/* ── Revenue Chart + Conversion Funnel ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.6fr 1fr", gap: 24, marginBottom: 24 }}>

        {/* Revenue Chart */}
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <h3 style={{ ...sectionTitle, marginBottom: 0 }}>Revenue Overview</h3>
            <ChartFilterBar value={revPeriod} onChange={setRevPeriod} />
          </div>
          {revChartLoading ? (
            <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#3b82f6", animation: "spin 0.7s linear infinite" }} />
            </div>
          ) : revChartData.length === 0 ? (
            <div style={{ height: 240, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
              <div style={{ fontSize: 20, marginBottom: 10 }}>No data</div>
              <div style={{ fontSize: 13 }}>No revenue for this period</div>
            </div>
          ) : (
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revChartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `₹${v}`} />
                  <RechartsTooltip
                    cursor={{ fill: "#f8fafc" }}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }}
                    formatter={(v: any, name: any) => [`₹${Number(v).toLocaleString()}`, name]}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue"    name="Revenue"    fill="#3b82f6" radius={[5, 5, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="commission" name="Commission" fill="#10b981" radius={[5, 5, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Conversion Funnel */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Conversion Funnel</h3>
          <div style={{ ...funnelContainer, marginTop: 8 }}>
            {funnelSteps.map((step, i) => (
              <FunnelStep
                key={i}
                label={step.label}
                val={step.val}
                widthPct={funnelBase > 0 ? (step.val / funnelBase) * 100 : 80 - i * 14}
                color={step.color}
                total={funnelBase}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Demographics + Top Scholars + Top Locations ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.2fr 1fr", gap: 24, marginBottom: 24 }}>

        {/* Religion Distribution */}
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
            <h3 style={{ ...sectionTitle, marginBottom: 0 }}>User Demographics</h3>
            <ChartFilterBar value={demoPeriod} onChange={setDemoPeriod} />
          </div>
          {pieData.length === 0 ? (
            <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>
              {loading ? <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#3b82f6", animation: "spin 0.7s linear infinite" }} /> : "No data"}
            </div>
          ) : (
            <>
              <div style={{ height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%" cy="50%"
                      innerRadius={50} outerRadius={80}
                      dataKey="value"
                      labelLine={false}
                      label={PieLabel}
                    >
                      {pieData.map((_: any, index: number) => (
                        <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(v: any, name: any) => [v, name]} contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", marginTop: 12 }}>
                {pieData.map((entry: any, i: number) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                    <span style={{ color: "#475569", fontWeight: 600 }}>{entry.name}</span>
                    <span style={{ color: "#94a3b8" }}>{entry.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Top Scholars */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Top Scholars</h3>
          {loading && scholars.length === 0 ? (
            <div style={{ fontSize: 13, color: "#94a3b8" }}>Loading…</div>
          ) : scholars.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8" }}>
              <div style={{ fontSize: 18, marginBottom: 8 }}>No entries</div>
              <div style={{ fontSize: 13 }}>No approved scholars yet</div>
            </div>
          ) : scholars.map((s: any, i: number) => (
            <ScholarItem
              key={i}
              rank={i + 1}
              name={s.name}
              religion={s.religion}
              rating={s.rating}
              bookings={s.totalBookings || 0}
              earnings={`₹${(s.totalEarnings || 0).toLocaleString()}`}
            />
          ))}
        </div>

        {/* Top Locations */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Top Locations</h3>
          {loading && locations.length === 0 ? (
            <div style={{ fontSize: 13, color: "#94a3b8" }}>Loading…</div>
          ) : locations.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8" }}>
              <div style={{ fontSize: 18, marginBottom: 8 }}>No entries</div>
              <div style={{ fontSize: 13 }}>No location data yet</div>
            </div>
          ) : locations.map((loc: any, i: number) => (
            <LocationItem
              key={i}
              rank={i + 1}
              name={loc.city}
              sub={loc.state || "India"}
              val={(loc.scholarCount || 0).toLocaleString()}
            />
          ))}
        </div>
      </div>

      {/* ── Retention ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1fr", gap: 24 }}>
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <h3 style={{ ...sectionTitle, marginBottom: 0 }}>User Retention</h3>
            <ChartFilterBar value={retPeriod} onChange={setRetPeriod} />
          </div>
          <RetentionBar label="7-Day Retention"  pct={retention?.retention7Day  || 0} color="#10b981" />
          <RetentionBar label="30-Day Retention" pct={retention?.retention30Day || 0} color="#3b82f6" />
          <RetentionBar label="90-Day Retention" pct={retention?.retention90Day || 0} color="#8b5cf6" />
          <div style={{
            marginTop: 24, padding: "16px 20px", borderRadius: 14,
            background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Repeat Booking Rate</div>
              <div style={{ fontSize: 11, color: "#93c5fd", marginTop: 2 }}>Customers with {">"} 1 booking</div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#2563eb" }}>
              {retention?.repeatBookingRate !== undefined ? `${retention.repeatBookingRate}%` : "—"}
            </div>
          </div>
        </div>

        {/* Quick stats summary */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Period Summary</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              { label: "New Users",     val: stats?.periodCurrent?.users    || 0, icon: "US", color: "#2563eb" },
              { label: "New Scholars",  val: stats?.periodCurrent?.scholars  || 0, icon: "SC", color: "#f59e0b" },
              { label: "New Bookings",  val: stats?.periodCurrent?.bookings  || 0, icon: "BK", color: "#10b981" },
              { label: "Revenue",       val: `₹${(stats?.periodCurrent?.revenue || 0).toLocaleString()}`, icon: "RV", color: "#8b5cf6", isStr: true },
            ].map((item, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "12px 16px", borderRadius: 12,
                background: `${item.color}08`, border: `1px solid ${item.color}20`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: `${item.color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  }}>{item.icon}</div>
                  <span style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>{item.label}</span>
                </div>
                <span style={{ fontSize: 18, fontWeight: 800, color: item.color }}>
                  {(item as any).isStr ? item.val : (item.val as number).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* ── MNC Operations & Commercial Health ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr", gap: 24, marginTop: 24 }}>
        {/* Marketplace KPIs */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Marketplace & Operational Efficiency</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            {[
              { label: "Average Order Value (AOV)", val: `₹${(operationalMetrics?.avgOrderValue || 0).toLocaleString()}`, color: "#3b82f6", desc: "Average amount spent per booking" },
              { label: "Scholar Utilization Rate", val: `${operationalMetrics?.scholarUtilization || 0}%`, color: "#10b981", desc: "Scholars with active bookings" },
              { label: "Booking Dispute/Cancel Rate", val: `${operationalMetrics?.cancellationRate || 0}%`, color: "#ef4444", desc: "Percentage of bookings cancelled" },
              { label: "LTV Index (Estimated)", val: `₹${((operationalMetrics?.avgOrderValue || 0) * (1 + (operationalMetrics?.repeatBookingRate || 0) / 100)).toFixed(2)}`, color: "#8b5cf6", desc: "Customer lifetime value multiplier" }
            ].map((metric, i) => (
              <div key={i} style={{ padding: 16, borderRadius: 14, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>{metric.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: metric.color, marginBottom: 4 }}>{metric.val}</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>{metric.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Commercial Revenue Segment Distribution */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Revenue Share by Religion Segment</h3>
          {!(operationalMetrics?.revenueByReligion) || operationalMetrics.revenueByReligion.length === 0 ? (
            <div style={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>
              No commercial transactions recorded yet
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {operationalMetrics.revenueByReligion.map((r: any, i: number) => {
                const total = operationalMetrics.revenueByReligion.reduce((sum: number, curr: any) => sum + (curr.amount || 0), 0) || 1;
                const pct = (((r.amount || 0) / total) * 100).toFixed(1);
                return (
                  <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
                      <span>{r.religion}</span>
                      <span>₹{(r.amount || 0).toLocaleString()} ({pct}%)</span>
                    </div>
                    <div style={{ height: 10, background: "#f1f5f9", borderRadius: 10, overflow: "hidden" }}>
                      <div style={{
                        width: `${pct}%`, height: "100%",
                        background: PIE_COLORS[i % PIE_COLORS.length],
                        borderRadius: 10,
                        transition: "width 1s ease"
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
